import config from "./config";
import { jwtDecode } from "jwt-decode";
import * as moment from "moment";

import axios from "axios";

class FastAPIClient {
    constructor(overrides) {
        this.config = {
            ...config,
            ...overrides,
        };
        this.authToken = config.authToken;
        this.login = this.login.bind(this);
        this.apiClient = this.getApiClient(this.config);
    }

    /* ----- Authentication & User Operations ----- */

    /* Authenticate the user with the backend services.
     * The same JWT should be valid for both the api and cms */
    login(username, password) {
        delete this.apiClient.defaults.headers["Authorization"];

        // HACK: This is a hack for scenario where there is no login form
        const form_data = new FormData();
        const grant_type = "password";
        const item = { grant_type, username, password };
        for (const key in item) {
            form_data.append(key, item[key]);
        }

        return this.apiClient.post("/auth/login", form_data).then((resp) => {
            localStorage.setItem("token", JSON.stringify(resp.data));
            return this.fetchUserName();
        });
    }

    fetchUser() {
        return this.apiClient.get("/auth/me").then(({ data }) => {
            localStorage.setItem("user", JSON.stringify(data));
            return data;
        });
    }

    fetchUserName() {
        return this.apiClient.get("/auth/me").then(({ data }) => {
            localStorage.setItem(
                "userfirstname",
                JSON.stringify(data["first_name"]).replace(/['"]+/g, "")
            );
            return data;
        });
    }

    getUser(user_id) {
        return this.apiClient.get(`/user/${user_id}`).then(({ data }) => {
            return data.user;
        });
    }

    // Logging out is just deleting the jwt.
    logout() {
        // Add here any other data that needs to be deleted from local storage
        // on logout
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("layoutData");
        localStorage.removeItem("language");
    }

    /* ----- Client Configuration ----- */

    /* Create Axios client instance pointing at the REST api backend */
    getApiClient(config) {
        const initialConfig = {
            baseURL: `${config.apiBasePath}/api/v1`,
        };
        const client = axios.create(initialConfig);

        // client.interceptors.request.use(localStorageTokenInterceptor);
        // Add the interceptor to Axios
        client.interceptors.request.use(
            (config) => {
                return localStorageTokenInterceptor(config);
            },
            (error) => {
                return Promise.reject(error);
            }
        );
        return client;
    }
    getProduct(productId) {
        return this.apiClient
            .get(`/product/individual/${productId}`)
            .then(({ data }) => {
                return data;
            });
    }

    getProducts(clientId) {
        return this.apiClient
            .get(`/reco/products/${clientId}?max_results=50000`)
            .then(({ data }) => {
                return data;
            });
    }

    countProducts({ category } = {}, { filter = {} } = {}) {
        return this.apiClient
            .post(`/product/count`, filter, { params: { category } })
            .then(({ data }) => {
                return data;
            });
    }

    getAllProductsOffline() {
        return this.apiClient.get("/product/").then(({ data }) => {
            return data;
        });
    }

    getProductLevelOnes() {
        return this.apiClient.get(`/product/levels`).then(({ data }) => {
            return data;
        });
    }

    getProductSubCategories(
        { category, showLeads } = {},
        { subcategories = {} } = {}
    ) {
        return this.apiClient
            .post("product/subfilters", subcategories, {
                params: { category, showLeads },
            })
            .then(({ data }) => {
                return data;
            });
    }

    getAllProducts(
        { skip, limit, search, category } = {},
        { filter = {} } = {}
    ) {
        return this.apiClient
            .post(`/product/`, filter, {
                params: {
                    skip,
                    limit,
                    search,
                    category,
                },
            })
            .then(({ data }) => {
                return data;
            });
    }
    getRecommendations(clientId) {
        return this.apiClient
            .get(`/recommendation/${clientId}`)
            .then(({ data }) => {
                return data;
            });
    }

    getProductSales(clientId) {
        return this.apiClient
            .get(`/productsales/${clientId}`)
            .then(({ data }) => {
                return data;
            });
    }

    getUsers(warehouse_id = null) {
        // Construct the URL with warehouse_id if provided
        let url = "/user/";

        // If warehouse_id is provided, add it to the query string
        if (warehouse_id) {
            url += `?warehouse_id=${warehouse_id}`;
        }

        return this.apiClient.get(url).then(({ data }) => {
            return data;
        });
    }
    getNotificationTypes() {
        return this.apiClient.get(`/notification-types/`).then(({ data }) => {
            return data;
        });
    }

    getSingleNotificationType(id) {
        return this.apiClient
            .get(`/notification-types/${id}`)
            .then(({ data }) => {
                return data;
            });
    }

    getUserNotifications(userId) {
        return this.apiClient
            .get(`/notifications/${userId}`)
            .then(({ data }) => {
                return data;
            });
    }

    markNotificationAsRead(notificationId, status) {
        return this.apiClient
            .put(`/notifications/read/${notificationId}`, { status })
            .then(({ data }) => {
                return data;
            });
    }

    markAllNotificationsAsRead(userId) {
        return this.apiClient
            .put(`/notifications/read-all/${userId}`)
            .then(({ data }) => {
                return data;
            });
    }

    deleteNotification(notificationId) {
        return this.apiClient.delete(`/notifications/${notificationId}`);
    }

    getMessagesNotifications(userId) {
        return this.apiClient
            .get(`messages/notifications/${userId}`)
            .then(({ data }) => {
                return data;
            });
    }

    updateMessagesNotifications(userId, clientId) {
        return this.apiClient
            .patch(`messages/notifications/${clientId}/${userId}/read`)
            .then(({ data }) => {
                return data;
            });
    }

    getLeads(clientId) {
        return this.apiClient.get(`/leads/${clientId}`).then(({ data }) => {
            return data;
        });
    }

    getReminder(clientId = null, userId = null) {
        const params = new URLSearchParams();

        if (clientId) {
            params.append("client_id", clientId);
        }
        if (userId) {
            params.append("user_id", userId);
        }

        const queryString = params.toString() ? `?${params.toString()}` : "";

        return this.apiClient
            .get(`/reminder/${queryString}`)
            .then(({ data }) => {
                return data;
            });
    }

    createReminder(data) {
        return this.apiClient.post(`/reminder/`, data);
    }

    deleteReminder(reminderId) {
        return this.apiClient.delete(`/reminder/${reminderId}`);
    }

    updateLead(leadId, leadUpdate) {
        return this.apiClient.put(`/leads/${leadId}`, leadUpdate);
    }

    updateRecommendation(recommendationId, recommendationupdate) {
        return this.apiClient.put(
            `/recommendation/${recommendationId}`,
            recommendationupdate
        );
    }

    updateUser(userId, userUpdate) {
        return this.apiClient.put(`/user/update/${userId}`, userUpdate);
    }

    getClients(keyword) {
        return this.apiClient
            .get(`/clients/search/?keyword=${keyword}&max_results=10`)
            .then(({ data }) => {
                return data;
            });
    }

    createUserSession(path, browser, device) {
        return this.apiClient
            .post("/user-sessions/", {
                path: path,
                browser: browser,
                device: device,
            })
            .then(({ data }) => {
                return data;
            });
    }

    getClient(id) {
        return this.apiClient.get(`/clients/${id}`).then(({ data }) => {
            return data;
        });
    }

    createClient(clientData) {
        return this.apiClient
            .post("/clients/", clientData) // POST request to create a new client
            .then(({ data }) => {
                return data; // Return the data from the response, which includes the created client
            });
        // .catch(error => {
        //   console.error('Error creating client:', error);
        //   throw error; // Rethrow or handle the error as needed
        // });s
    }

    createNote(noteData) {
        return this.apiClient
            .post("/notes/", noteData) // POST request to create a new note
            .then(({ data }) => {
                return data; // Return the data from the response, which includes the created note
            });
    }

    fetchNotes(clientId) {
        return this.apiClient
            .get(`/notes/${clientId}`) // POST request to create a new note
            .then(({ data }) => {
                return data; // Return the data from the response, which includes the created note
            });
    }

    updateNote(noteId, noteData) {
        return this.apiClient
            .put(`/notes/${noteId}`, noteData)
            .then(({ data }) => data);
    }

    // Function to create a kilometer log
    createKilometer(payload) {
        return this.apiClient
            .post(`/kilometer_logs/`, payload) // POST request to create a new kilometer log
            .then(({ data }) => {
                return data; // Return the data from the response, which includes the created kilometer log
            });
    }

    // Function to fetch kilometer logs
    fetchKilometers(userId, date) {
        return this.apiClient
            .get(`/kilometer_logs/`, {
                params: { user_id: userId, date: date }, // Send user_id and date as query params
            })
            .then(({ data }) => {
                return data; // Return the data from the response, which includes the logs
            });
    }

    updateKilometer(payload) {
        return this.apiClient
            .put(`/kilometer_logs/`, payload) // PUT request to update the kilometer log for the specific user and date
            .then(({ data }) => {
                return data; // Return the data from the response, which includes the updated kilometer log
            });
    }

    fetchUserActivity(startDate, endDate) {
        return this.apiClient
            .get(`/data/user_activity/`, {
                params: {
                    start_date: startDate,
                    end_date: endDate,
                },
            })
            .then(({ data }) => {
                return data;
            });
    }

    fetchUserKilometers() {
        return this.apiClient.get(`/data/user_kilometers/`).then(({ data }) => {
            return data;
        });
    }

    fetchUserClientVisits() {
        return this.apiClient
            .get(`/data/user_client_visits/`)
            .then(({ data }) => {
                return data;
            });
    }

    fetchUserMeetings() {
        return this.apiClient
            .get(`/data/user_meetings/`) // Update the endpoint to '/data/user_meetings/'
            .then(({ data }) => {
                return data;
            });
    }

    fetchTeamMembers() {
        return this.apiClient.get(`/user/team_members/`).then(({ data }) => {
            return data;
        });
    }

    fetchUserAnalytics() {
        return this.apiClient.get(`/data/user_analytics/`).then(({ data }) => {
            return data;
        });
    }

    fetchMonthlyStats({ startDate, endDate }) {
        return this.apiClient
            .get(`/data/monthly_stats/`, {
                params: {
                    start_date: startDate,
                    end_date: endDate,
                },
            })
            .then(({ data }) => data);
    }

    fetchMonthlyCoverage() {
        return this.apiClient
            .get(`/data/monthly_coverage/`)
            .then(({ data }) => {
                return data;
            });
    }

    fetchCoverage(startDate, endDate, selectedWarehouse, selectedUser) {
        return this.apiClient
            .get(`/data/client_coverage_for_period/`, {
                params: {
                    start_date: startDate,
                    end_date: endDate,
                    selectedWarehouse: selectedWarehouse,
                    selectedUser: selectedUser,
                },
            })
            .then(({ data }) => {
                return data;
            });
    }

    fetchClientInteractionStats(
        startDate,
        endDate,
        selectedWarehouse,
        selectedUser
    ) {
        return this.apiClient
            .get(`/data/client_interactions/`, {
                params: {
                    start_date: startDate,
                    end_date: endDate,
                    selectedWarehouse: selectedWarehouse,
                    selectedUser: selectedUser,
                },
            })
            .then(({ data }) => {
                return data;
            });
    }

    fetchNumericDistribution(
        startDate,
        endDate,
        selectedWarehouse,
        selectedUser
    ) {
        return this.apiClient
            .get(`/data/numeric_distribution/`, {
                params: {
                    start_date: startDate,
                    end_date: endDate,
                    selectedWarehouse: selectedWarehouse,
                    selectedUser: selectedUser,
                },
            })
            .then(({ data }) => {
                return data;
            });
    }

    fetchClients(startDate, endDate) {
        return this.apiClient
            .get(`/data/clients/`, {
                params: {
                    start_date: startDate,
                    end_date: endDate,
                },
            })
            .then(({ data }) => {
                return data;
            });
    }

    fetchLeads(startDate, endDate) {
        return this.apiClient
            .get(`/data/leads/`, {
                params: {
                    start_date: startDate,
                    end_date: endDate,
                },
            })
            .then(({ data }) => {
                return data;
            });
    }

    fetchUserTypes() {
        return this.apiClient.get(`/data/user_types/`).then(({ data }) => {
            return data;
        });
    }

    fetchBrandStats() {
        return this.apiClient.get(`/data/brands/`).then(({ data }) => {
            return data;
        });
    }

    fetchClientUsers(clientId) {
        return this.apiClient
            .get(`/user_access/${clientId}`) // POST request to create a new note
            .then(({ data }) => {
                return data; // Return the data from the response, which includes the created note
            });
    }

    updateClientUsers(clientId, userWithAccess) {
        return this.apiClient
            .put(`/user_access/${clientId}`, userWithAccess)
            .then(({ data }) => data);
    }

    // Delete a note
    deleteNote(noteId) {
        return this.apiClient.delete(`/notes/${noteId}`).then(() => null); // No response data expected on delete
    }

    updateClient(clientData) {
        return this.apiClient.put(`/clients/`, clientData);
    }

    getClientNew(id) {
        return this.apiClient
            .get(`/clients/full_details/${id}`)
            .then(({ data }) => {
                return data;
            });
    }

    searchClients(user_id = null, search) {
        return this.apiClient
            .get(`/clients/`, {
                params: { search, user_id },
            })
            .then(({ data }) => {
                return data.results;
            });
    }

    getClientHistory(id) {
        return this.apiClient
            .get(`/clients/history/?id=${id}`)
            .then(({ data }) => {
                return data;
            });
    }

    getDocuments(clientId) {
        return this.apiClient
            .get(`/documents/status/${clientId}`)
            .then(({ data }) => {
                return data;
            });
    }

    updateDocuments(documents) {
        return this.apiClient
            .put(`/documents/`, { documents })
            .then(({ data }) => {
                return data;
            });
    }

    getLayout() {
        return this.apiClient.get(`/layout/`).then(({ data }) => {
            return data;
        });
    }

    // Send a new message
    sendMessage(messageData) {
        return this.apiClient
            .post(`/messages/`, messageData)
            .then(({ data }) => {
                return data;
            })
            .catch((error) => {
                console.error("Error sending message:", error);
                throw error;
            });
    }

    // Fetch messages for a chat
    getMessages(client_id) {
        return this.apiClient
            .get(`/messages/`, { params: { client_id } })
            .then(({ data }) => {
                return data.results;
            })
            .catch((error) => {
                console.error("Error fetching messages:", error);
                throw error;
            });
    }

    getUserClients(user_id = null, client_id = null) {
        // Create the API endpoint URL
        let url = `/clients/full_details/`;

        // Build query parameters
        const params = new URLSearchParams();
        if (user_id) params.append("user_id", user_id);
        if (client_id) params.append("client_id", client_id);

        // Add query parameters to URL if any exist
        const queryString = params.toString();
        if (queryString) {
            url += `?${queryString}`;
        }

        // Make the API request
        return this.apiClient.get(url).then(({ data }) => {
            return data;
        });
    }

    getUserClientsOnline(user_id = null, client_id = null) {
        // Create the API endpoint URL
        let url = `/clients/user_clients/`;

        // If a user_id is provided, append it to the URL
        if (user_id) {
            url += `?user_id=${user_id}`;
        }
        if (client_id) {
            url += `?client_id=${client_id}`;
        }

        // Make the API request
        return this.apiClient.get(url).then(({ data }) => {
            return data;
        });
    }

    getLeadFormLayout() {
        // Make the API request
        return this.apiClient.get("/lead_form/").then(({ data }) => {
            return data;
        });
    }

    getClientLeadForm(clientId) {
        // Make the API request
        return this.apiClient
            .get(`/lead_form/lead_form/?client_id=${clientId}`)
            .then(({ data }) => {
                return data;
            });
    }

    getClientLeadContracts(clientId) {
        // Fetch all lead contracts for a given client ID
        return this.apiClient
            .get(`/lead_contracts/?client_id=${clientId}`)
            .then(({ data }) => {
                return data;
            });
    }

    updateClientLeadContracts(clientId, contracts) {
        // Update or insert lead contracts for a given client ID
        return this.apiClient
            .put(`/lead_contracts/?client_id=${clientId}`, contracts)
            .then(({ data }) => {
                return data;
            });
    }

    updateClientInterest(clientId, leadForm) {
        // Ensure clientId and leadForm are properly passed
        return this.apiClient
            .put(
                `/lead_form/product_interests/?client_id=${clientId}`,
                leadForm
            )
            .then(({ data }) => {
                return data;
            });
    }

    getUserClientswInfo(params) {
        const {
            skip,
            limit,
            client_id,
            user_id,
            warehouse_id,
            is_lead,
            filter,
            status_filter,
            visit_days,
            sort_by,
        } = params;

        // Query parameters
        const queryParams = {
            skip,
            limit,
            client_id,
            user_id,
            warehouse_id,
            is_lead,
            sort_by,
        };

        // Body parameters
        const bodyParams = {
            filter,
            status_filter,
            visit_days,
        };

        return this.apiClient
            .post("/clients/user_clients_w_info/", bodyParams, {
                params: queryParams,
            })
            .then(({ data }) => {
                return data;
            });
    }

    getClientCount(
        { user_id, is_lead } = {},
        { filter = {}, status_filter = {}, visit_days = [] } = {}
    ) {
        return this.apiClient
            .post(
                "/clients/count",
                {
                    filter,
                    status_filter,
                    visit_days,
                },
                {
                    params: { user_id, is_lead },
                }
            )
            .then(({ data }) => {
                return data;
            });
    }

    getClientSubCategories(
        { user_id, showLeads } = {},
        { subcategories = {} } = {}
    ) {
        return this.apiClient
            .post("/clients/subfilters", subcategories, {
                params: { user_id, showLeads },
            })
            .then(({ data }) => {
                return data;
            });
    }

    getCharts() {
        return this.apiClient.get(`/charts/`).then(({ data }) => {
            return data;
        });
    }

    getReceipts(client_id) {
        return this.apiClient
            .get(`/receipt/?client_id=${client_id}`)
            .then(({ data }) => {
                return data;
            });
    }

    getProductClientOrders(client_id) {
        return this.apiClient
            .get(`/product_client_orders/${client_id}`)
            .then(({ data }) => {
                return data;
            });
    }

    getReportLayout(meeting_type_id = null) {
        // Conditionally set the endpoint based on whether meeting_type_id is provided
        const url = meeting_type_id
            ? `/reports/layout/${meeting_type_id}` // If meeting_type_id is provided, use it in the URL
            : "/reports/layout/"; // If no meeting_type_id, fetch all layouts

        return this.apiClient.get(url).then(({ data }) => {
            return data;
        });
    }

    saveReport(reportdata) {
        return this.apiClient.post(`/reports/`, reportdata);
    }

    getClientStats(client_id) {
        return this.apiClient
            .get(`/client_stats/?client_id=${client_id}`)
            .then(({ data }) => {
                return data;
            });
    }

    resetNBA(recommendationId) {
        return this.apiClient.put(`/recommendation/reset/${recommendationId}`);
    }

    resetLeads() {
        return this.apiClient.put(`/leads/reset/`);
    }

    getMeetings(user_id, client_id, begin_time_frame, end_time_frame) {
        // Set up query parameters object, adding user_id, begin_time_frame, and end_time_frame if they exist
        const params = {};

        if (user_id) params.user_id = user_id;
        if (begin_time_frame) params.begin_time_frame = begin_time_frame;
        if (end_time_frame) params.end_time_frame = end_time_frame;
        if (client_id) params.client_id = client_id;

        // Pass the params as query parameters to the get request
        return this.apiClient.get(`/meetings/`, { params });
    }

    getMeetingTypes() {
        // Pass the params as query parameters to the get request
        return this.apiClient.get(`/meetings/meeting_types/`);
    }

    createMeeting(meeting) {
        // Set up query parameters object, adding user_id if it exists
        // const params = user_id ? { user_id } : {};

        // Pass the params as query parameters to the get request
        return this.apiClient.post(`/meetings/`, meeting);
    }

    updateMeeting(meeting_id, meeting) {
        return this.apiClient.put(`/meetings/${meeting_id}`, meeting);
    }

    deleteMeeting(meeting_id) {
        // Construct the endpoint URL with the meeting_id
        return this.apiClient.delete(`/meetings/${meeting_id}`);
    }

    deleteMeetingsByRecurrence(recurring_id, meeting_start_date) {
        // Construct the endpoint URL with the meeting_id
        return this.apiClient.delete(`/meetings/recurrence/${recurring_id}`, {
            params: { meeting_start_date: meeting_start_date },
        });
    }

    getUnreadAnnouncements(userId) {
        return this.apiClient.get(`/announcements/${userId}`);
    }

    markAnnouncementsAsRead(userId) {
        return this.apiClient.put(`/announcements/${userId}`);
    }

    resetClients() {
        return this.apiClient.put(`/clients/reset/`);
    }

    // createClient(label, url, source, submitter_id) {
    //   const clientData = {
    //     label,
    //     url,
    //     source,
    //     submitter_id: submitter_id,
    //   };
    //   return this.apiClient.post(`/clients/`, clientData);
    // }

    updateAlert(recommendationupdate) {
        return this.apiClient.put(`/clients/`, recommendationupdate);
    }

    getLeadNotifications() {
        return this.apiClient.get(`/leads/lead_count/`).then(({ data }) => {
            return data;
        });
    }

    getRecommendationNotifications() {
        return this.apiClient
            .get(`/recommendation/notification_count/`)
            .then(({ data }) => {
                return data;
            });
    }

    getRecommendationValue() {
        return this.apiClient
            .get(`/recommendation/recommendation_value/`)
            .then(({ data }) => {
                return data;
            });
    }

    getContactPermissions(userId) {
        return this.apiClient
            .get(`/permissions/contact_permissions/?user_id=${userId}`)
            .then(({ data }) => {
                return data;
            });
    }

    getReports(
        client_id,
        warehouse_id = null,
        selectedWarehouse = null,
        selectedUser = null,
        startDate = null,
        endDate = null
    ) {
        const params = {};

        if (client_id) params.client_id = client_id;
        if (warehouse_id) params.warehouse_id = warehouse_id;
        if (selectedWarehouse) params.selectedWarehouse = selectedWarehouse;
        if (selectedUser) params.selectedUser = selectedUser;
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;

        return this.apiClient.get(`/reports/`, { params }).then(({ data }) => {
            return data;
        });
    }

    getClientProductCategories() {
        // Construct the base URL
        let url = "/data/";

        return this.apiClient.get(url).then(({ data }) => {
            return data;
        });
    }

    getClientCoverage() {
        // Construct the URL with an optional warehouse_id query parameter
        let url = "/data/client_coverage/";

        return this.apiClient.get(url).then(({ data }) => {
            return data;
        });
    }

    getManagementDashboard(userRoleId = null, warehouseId = null) {
        // Build query parameters if filters are provided
        let queryParams = "";

        if (userRoleId || warehouseId) {
            queryParams = `?`;

            if (userRoleId) {
                queryParams += `user_role_id=${encodeURIComponent(userRoleId)}`;
            }

            if (warehouseId) {
                if (userRoleId) queryParams += `&`; // If userRoleId exists, add an ampersand
                queryParams += `warehouse_id=${encodeURIComponent(
                    warehouseId
                )}`;
            }
        }

        // Make the GET request with optional query parameters
        return this.apiClient
            .get(`/management_dashboard/${queryParams}`)
            .then(({ data }) => {
                return data;
            });
    }

    getUserStats(user_id) {
        return this.apiClient
            .get(`/user_stats/?user_id=${user_id}`)
            .then(({ data }) => {
                return data;
            });
    }

    sendMail(mailData) {
        return this.apiClient.post(`/sendgrid/`, mailData);
    }

    fetchLeadTable() {
        return this.apiClient.get(`/data/lead-table/`).then(({ data }) => {
            return data;
        });
    }

    fetchClientBrandProductStats() {
        return this.apiClient
            .get("/data/clientbrandproductstats")
            .then(({ data }) => {
                return data;
            });
    }
}

// Modify your localStorageTokenInterceptor function to work with Axios interceptors
function localStorageTokenInterceptor(request) {
    return new Promise((resolve) => {
        // Logging all elements in localStorage

        const headers = {};
        var tokenData_web = localStorage.getItem(
            "@@auth0spajs@@::KKbOZIMeOk56DOxNpnwYCaEvwhnnoZjR::https://www.api.myway.technology::openid profile read:appointments"
        );
        var tokenData = localStorage.getItem("token");

        // Check if tokenData is empty or null
        if (
            (tokenData == null || tokenData == undefined) &&
            (tokenData_web == null || tokenData_web == undefined)
        ) {
            // Wait for 3 seconds before retrying

            setTimeout(() => {
                // Call the function again recursively

                localStorageTokenInterceptor(request).then(resolve); // Call recursively after timeout
            }, 1000); // 10000 milliseconds = 10 seconds
        } else {
            const token = { access_token: "", token_type: "bearer" };

            if (tokenData_web == null || tokenData_web == undefined) {
                token.access_token = tokenData;
            } else {
                const tokenObject = JSON.parse(tokenData_web);
                token.access_token = tokenObject.body.access_token;
            }

            if (token) {
                // const token = JSON.parse(tokenString);
                const decodedAccessToken = jwtDecode(token.access_token);
                const isAccessTokenValid =
                    moment.unix(decodedAccessToken.exp).toDate() > new Date();
                if (isAccessTokenValid) {
                    headers["Authorization"] = `Bearer ${token.access_token}`;
                } else {
                    console.log("expired");
                }
            }
            request.headers = headers;
            resolve(request); // Resolve the promise with the modified request object
        }
    });
}

export default FastAPIClient;
